import * as React from "react";
import { Slot } from "radix-ui";
import { cn } from "./cn";

// Button — rebuilt on v1's own `.btn` (web/panel/index.html): a bordered surface button by
// default, accent-filled for the primary act, soft-tinted for a secondary emphasis, and
// crit-coloured for destructive. 9px radius, 13.5px semi-bold label, border brightens to the
// accent line on hover. The first v2 pass used framework-default greys and looked it.

export type ButtonVariant = "default" | "accent" | "soft" | "danger" | "ghost";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
}

const variantClass: Record<ButtonVariant, string> = {
  default: "bg-surface text-fg border-line2 hover:border-[var(--ozp-accent-line)]",
  accent: "bg-accent text-onaccent border-transparent hover:brightness-110",
  soft: "bg-accentsoft text-accent border-[var(--ozp-accent-line)] hover:brightness-125",
  // Destructive is bordered-and-coloured rather than filled: a red slab next to three grey
  // buttons pulls the eye to the one action nobody should click by accident. It fills only
  // on hover, once the pointer is already committed to it.
  danger: "bg-transparent text-crit border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] hover:bg-critsoft",
  ghost: "bg-transparent text-muted border-transparent hover:text-fg hover:bg-surface2",
};

// On a touch screen every button is at least 40px (sm) or 44px (md) tall — Apple's 44pt and
// Android's 48dp are the floor a thumb needs. Measured 2026-11-02: every row action was ~30px.
const sizeClass: Record<ButtonSize, string> = {
  sm: "px-2.5 py-1.5 text-[12.5px] gap-1.5 pointer-coarse:min-h-10 pointer-coarse:px-3",
  md: "px-[15px] py-[9px] text-[13.5px] gap-[7px] pointer-coarse:min-h-11",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "md", asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot.Root : "button";
    return (
      <Comp
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center whitespace-nowrap rounded-[9px] border font-medium",
          "transition-[background-color,border-color,filter,transform] duration-150",
          "active:translate-y-px disabled:pointer-events-none disabled:opacity-40",
          variantClass[variant],
          sizeClass[size],
          className,
        )}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
